/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { loadable } from '../utils/loadable';
import { bbox, featureCollection } from '@turf/turf';
import type { Feature, FeatureCollection, Polygon } from 'geojson';
import { canopyChunksSelector, useSetPostDataChunksUsers } from './selectors';
import { CanopyProperties } from './types';
import { POSTProperties } from '../Post/types';
import type { RGBAColor } from '../map/deckgl-types';
import { useGridCellLayer } from '../map/GridCellLayer';
import React from 'react';
import { POST_EVENT_OF, primaryKeyForSection } from '../Post/PostLayer';
import { cameraSizedAtom } from '../map/atoms';
import { useSearchParams } from 'react-router-dom';
import { selectedFeatureAtom } from '../Incident/FeaturePopup';
import { cameraFlightAtom } from '../map/flyToFeature';

type Props = {
  extruded: boolean;
  visible: boolean;
  /**
   * Whether this mount needs the canopy POST table at all. The hook is mounted
   * by the shared Map component on every route; `false` (incident views, where
   * the canopy cells never draw) skips the fetch instead of hiding its result.
   */
  enabled: boolean;
};

export const useCanopyLayer = (props: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const postData = useAtomValue(loadable(canopyChunksSelector));
  const callback = useSetPostDataChunksUsers(postData.state === 'hasData' ? postData.data : []);
  const [selectedPost] = useAtom(selectedFeatureAtom);
  const flyTo = useSetAtom(cameraFlightAtom);
  const cameraSized = useAtomValue(cameraSizedAtom);
  const primaryKey = useAtomValue(primaryKeyForSection('canopy-post'));
  const canopyKmlId = searchParams.get('canopy_kml_id');

  // One-shot per visit to the canopy view: a later data refresh (or a camera
  // that only received its size after the data) must not snap the camera away
  // from wherever the user has taken it since.
  const framedCellsRef = React.useRef(false);

  React.useEffect(() => {
    if (!props.visible) {
      framedCellsRef.current = false;
    }
    if (postData.state === 'hasData') {
      // A collection can carry AGOL's paging flag as a GeoJSON foreign member; a
      // truncated last page means the chunk store is incomplete.
      const contents: (FeatureCollection<Polygon, POSTProperties> & {
        properties?: { exceededTransferLimit?: boolean };
      })[] = postData.data;
      if (
        contents.length === 0 ||
        contents?.[contents.length - 1]?.properties?.exceededTransferLimit
      ) {
        // Only the views that draw the canopy cells pay for the fetch.
        if (props.enabled) {
          callback();
        }
      } else {
        // Frame the loaded cells, unless the URL names one swath, which the
        // effect below frames instead. An empty table has nothing to frame, and
        // the fit waits for the camera to carry the canvas size it fits into.
        if (
          props.visible &&
          canopyKmlId == null &&
          contents[0].features.length > 0 &&
          cameraSized &&
          !framedCellsRef.current
        ) {
          framedCellsRef.current = true;
          flyTo({ bounds: bbox(contents[0]) });
        }
      }
    }
  }, [postData, callback, props.enabled, props.visible, flyTo, canopyKmlId, cameraSized]);

  const canopyFeature = React.useMemo(() => {
    if (postData.state === 'hasData' && postData.data.length) {
      const canopyFeatures = postData.data
        .flatMap(chunk => chunk.features.map(feature => feature))
        .filter(feature => {
          return feature.properties.kml_id === canopyKmlId;
        });

      return featureCollection(canopyFeatures);
    }

    return featureCollection([]);
  }, [postData, canopyKmlId]);

  React.useEffect(() => {
    if (canopyFeature.features.length > 0 && canopyKmlId != null) {
      const [minLng, minLat, maxLng, maxLat] = bbox(canopyFeature);

      searchParams.set('canopy_kml_id', canopyKmlId);
      searchParams.set('bbox', `${minLng},${minLat},${maxLng},${maxLat}`);
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, canopyFeature, canopyKmlId, setSearchParams]);

  const getFillColor = React.useCallback(
    (d: Feature<Polygon, CanopyProperties>): RGBAColor => {
      if (selectedPost?.id === d.id) {
        return [0, 255, 100, 180];
      }

      return getPostFillColor(d);
    },
    [selectedPost]
  );

  const GridCellLayer = useGridCellLayer<Feature<Polygon, CanopyProperties>>({
    id: 'canopy-post',
    // @ts-expect-error
    data: postData.state === 'hasData' ? postData.data : [],
    getFillColor: primaryKey === 'priority_rank' ? getFillColor : undefined,
    eventOf: POST_EVENT_OF.canopy,
    extruded: props.extruded,
    visible: props.visible,
  });

  if (postData.state !== 'hasData' || postData.data.length === 0) {
    return null;
  }

  return GridCellLayer;
};

const postColorRange: RGBAColor[] = [
  [181, 9, 9, 190], // #B50909
  [219, 129, 0, 190], // #DB8100
  [226, 178, 0, 190], // #E2B200
  [143, 195, 201, 190], // #8FC3C9
  [95, 133, 188, 190], // #5F85BC
];

export const getPostFillColor = (d: Feature<Polygon, CanopyProperties>): RGBAColor => {
  const { priority_rank } = d.properties;
  switch (priority_rank) {
    case 1:
      return postColorRange[0];
    case 2:
      return postColorRange[1];
    case 3:
      return postColorRange[2];
    case 4:
      return postColorRange[3];
    case 5:
      return postColorRange[4];
    default:
      return [0, 0, 0, 0];
  }
};

export const postKeyToLabel = {
  event_id: 'Grouped IPAWS event',
  usng5km: '5km USNG cell',
  state: 'State',
  county: 'County',
  fips: 'County FIPS code',
  ls_day_pop: 'LandScan Daytime Population',
  ls_night_pop: 'LandScan Nighttime Population',
  pop: 'Population',
  households: 'Number of Households',
  housingunits: 'Number of Housing units',
  pop65over: 'Population age 65 and over',
  pop16overunemployed: 'Population age 16+ and unemployed',
  pop16overnotinlaborforce: 'Population 16+ not in labor force',
  popamindianalaskan: 'Population American Indian or Alaska Native',
  householdsinpoverty: 'Number of Households in poverty',
  householdsdsfs: 'Number of Households on disability and food stamps',
  householdsdsnofs: 'Number of Households on disability no food stamps',
  householdswithfs: 'Number of Households with food stamps/SNAP',
  householdswithpa: 'Number of Households with public assistance',
  housingunitsmobilehomes: 'Number of Housing units that are mobile homes',
  lep: 'Population with Limited English Proficiency',
  event: 'IPWAS event type',
  effective: 'Effective',
  expires: 'Expires',
  quantile_sum: 'Rank Sum',
  final_rank: 'Unscaled Rank',
  priority_rank: 'POST Priority',
  run_time: 'POST run time',
  CommercialOfficeImpacts: 'Commercial Office Impact',
  ExposureScore: 'Priority Rank',
  ImpactScore: 'Impact Score',
  PercentOut: 'Percent Out',
  RankPotentialPowerOutageImpact: 'Rank Potential Power Outage Impact',
  ef_rank: 'Enhanced Fujita Rank',
  state_intersect: 'State Intersect',
} as const;
